import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { AlertCircle } from 'lucide-react';

const splitShiftConfigSchema = z.object({
  enabled: z.boolean(),
  minimumGapMinutes: z.coerce.number().min(0, "Must be at least 0").max(480, "Must be at most 480"),
  minimumShiftLengthMinutes: z.coerce.number().min(0, "Must be at least 0").max(720, "Must be at most 720"),
  countMultiplePerDay: z.boolean()
});

type SplitShiftConfigFormValues = z.infer<typeof splitShiftConfigSchema>;

interface SplitShiftSettingsFormProps {
  initialData: SplitShiftConfigFormValues;
  onSubmit: (data: SplitShiftConfigFormValues) => Promise<void>;
  isLoading?: boolean;
}

export function SplitShiftSettingsForm({ initialData, onSubmit, isLoading }: SplitShiftSettingsFormProps) {
  const form = useForm<SplitShiftConfigFormValues>({
    resolver: zodResolver(splitShiftConfigSchema),
    defaultValues: initialData
  });

  const enabled = form.watch('enabled');
  const minimumGapMinutes = form.watch('minimumGapMinutes');
  const minimumShiftLengthMinutes = form.watch('minimumShiftLengthMinutes');
  const countMultiplePerDay = form.watch('countMultiplePerDay');

  // Sync form with fetched settings when initialData changes
  useEffect(() => {
    form.reset(initialData);
  }, [initialData, form]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Split-Shift Detection</CardTitle>
        <CardDescription>
          Configure how split shifts are detected for allowance calculations
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Enable/Disable Toggle */}
            <FormField
              control={form.control}
              name="enabled"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Enable Split-Shift Detection</FormLabel>
                    <FormDescription>
                      Automatically detect and track split shifts for allowance calculations
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Minimum Gap Minutes */}
            <FormField
              control={form.control}
              name="minimumGapMinutes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Minimum Gap Between Shifts (minutes)</FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      {...field}
                      disabled={!enabled}
                      className="max-w-xs"
                    />
                  </FormControl>
                  <FormDescription>
                    The minimum time gap required between shifts to count as a split shift (0-480 minutes)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Minimum Shift Length */}
            <FormField
              control={form.control}
              name="minimumShiftLengthMinutes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Minimum Shift Length (minutes)</FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      {...field}
                      disabled={!enabled}
                      className="max-w-xs"
                    />
                  </FormControl>
                  <FormDescription>
                    Minimum duration for a shift to qualify for split-shift detection (0-720 minutes)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Count Multiple Per Day */}
            <FormField
              control={form.control}
              name="countMultiplePerDay"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Count Multiple Splits Per Day</FormLabel>
                    <FormDescription>
                      If enabled, counts all split shifts in a day. If disabled, only counts the first split.
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch 
                      checked={field.value} 
                      onCheckedChange={field.onChange}
                      disabled={!enabled}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Example Preview */}
            {enabled && (
              <div className="bg-muted p-4 rounded-lg border border-border">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium mb-2">Example:</p>
                    <p className="text-sm text-muted-foreground">
                      With current settings, a split shift occurs when an employee works two shifts 
                      (each at least {minimumShiftLengthMinutes} minutes) 
                      with a gap of at least {minimumGapMinutes} minutes between them.
                      {countMultiplePerDay 
                        ? ' All split shifts in a day will be counted.'
                        : ' Only the first split shift per day will be counted.'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <Button type="submit" disabled={isLoading || !form.formState.isDirty}>
              {isLoading ? 'Saving...' : 'Save Split-Shift Settings'}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
